import axios, { AxiosInstance } from 'axios';
import { getAuthToken } from '../config/firebase';

// New Channel-based Types
export interface Channel {
  _id: string;
  name: string;
  description?: string;
  category?: string;
  status: 'active' | 'inactive' | 'draft' | 'archived';
  logoUrl?: string;
  bannerUrl?: string;
  colorScheme?: Record<string, string>;
  isPublic: boolean;
  allowComments: boolean;
  moderationLevel: string;
  ownerId: string;
  moderators: string[];
  createdBy?: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface Show {
  _id: string;
  channelId: string;
  name: string;
  description?: string;
  category?: string;
  status: 'active' | 'inactive' | 'draft' | 'archived';
  frequency: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom';
  customSchedule?: Record<string, any>;
  timezone: string;
  newsCriteria: Record<string, any>;
  panelistIds: string[];
  discussionSettings: Record<string, any>;
  logoUrl?: string;
  introAudioUrl?: string;
  outroAudioUrl?: string;
  createdBy?: string;
  isPublic: boolean;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface Episode {
  _id: string;
  showId: string;
  channelId: string;
  title: string;
  description?: string;
  episodeNumber?: number;
  seasonNumber?: number;
  status: 'draft' | 'published' | 'archived';
  publishDate?: string;
  publishedAt?: string;
  duration?: number;
  wordCount?: number;
  segmentCount?: number;
  isPublic: boolean;
  allowComments: boolean;
  createdBy?: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}


export interface Panelist {
  _id: string;
  name: string;
  gender: string;
  age?: number;
  ttsProvider: string;
  voiceId?: string;
  voiceName: string;
  languageCode: string;
  openaiVoiceSettings: {
    speed?: number;
    format?: string;
    instructions?: string;
  };
  character: string;
  background?: string;
  expertise: string[];
  discussionStyle: {
    tone?: string;
    pace?: string;
    style?: string;
    approach?: string;
    speaking_style?: string;
    formality?: string;
  };
  headshotUrl?: string;
  createdBy?: string;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ScriptPart {
  id: string;
  panelistId: string;
  panelistName: string;
  script: string;
  order: number;
  audioUrl?: string;
}

export interface ShowSegment {
  _id: string;
  showId: string;
  title: string;
  description: string;
  order: number;
  script: string | null;
  scriptParts: ScriptPart[];
  audioUrl: string | null;
  audioDuration: number | null;
  status: 'draft' | 'script_ready' | 'audio_ready' | 'published';
  scriptGenerationId: string | null;
  audioGenerationStatus: 'pending' | 'generating' | 'completed' | 'failed';
  audioGenerationError: string | null;
  createdBy: string;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE_URL || '/api/v1';
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 120000, // 2 minutes
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(async (config) => {
      const token = await getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        // Fallback to test token for development
        config.headers.Authorization = 'Bearer test-token';
      }
      return config;
    });

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('API Error:', error);
        return Promise.reject(error);
      }
    );
  }

  // Channel APIs (for consumption app, only public channels)
  async getChannels(limit = 50, skip = 0): Promise<Channel[]> {
    const response = await this.api.get(`/channels?limit=${limit}&skip=${skip}&status=active`);
    return response.data;
  }

  async getChannel(id: string): Promise<Channel> {
    const response = await this.api.get(`/channels/${id}`);
    const channel = response.data;
    
    // For the consumption app, only allow access to public channels
    if (!channel.isPublic) {
      throw new Error('Channel is not available for public consumption');
    }
    
    return channel;
  }

  // Show APIs (for consumption app, only active shows in public channels)
  async getShows(limit = 50, skip = 0, channelId?: string): Promise<Show[]> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    params.append('skip', skip.toString());
    params.append('status', 'active');
    if (channelId) params.append('channel_id', channelId);
    
    const response = await this.api.get(`/shows-new/?${params.toString()}`);
    return response.data;
  }

  async getShow(id: string): Promise<Show> {
    const response = await this.api.get(`/shows-new/${id}`);
    const show = response.data;
    
    // For the consumption app, only allow access to active shows
    if (show.status !== 'active') {
      throw new Error('Show is not available for public consumption');
    }
    
    return show;
  }

  // Episode APIs (for consumption app, only published episodes)
  async getEpisodes(limit = 50, skip = 0, showId?: string, channelId?: string): Promise<Episode[]> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    params.append('skip', skip.toString());
    params.append('status', 'published');
    if (showId) params.append('show_id', showId);
    if (channelId) params.append('channel_id', channelId);
    
    const response = await this.api.get(`/episodes?${params.toString()}`);
    return response.data;
  }

  async getEpisode(id: string): Promise<Episode> {
    const response = await this.api.get(`/episodes/${id}`);
    const episode = response.data;
    
    // For the consumption app, only allow access to published episodes
    if (episode.status !== 'published') {
      throw new Error('Episode is not available for public consumption');
    }
    
    return episode;
  }


  // Panelists API
  async getPanelists(limit = 50, skip = 0): Promise<Panelist[]> {
    const response = await this.api.get(`/panelists?limit=${limit}&skip=${skip}`);
    return response.data;
  }

  async getPanelist(id: string): Promise<Panelist> {
    const response = await this.api.get(`/panelists/${id}`);
    return response.data;
  }

  // Show Segments API (now works with episodes)
  async getShowSegments(episodeId: string): Promise<ShowSegment[]> {
    const response = await this.api.get(`/episodes/${episodeId}/segments`);
    return response.data;
  }

  async getShowSegment(segmentId: string): Promise<ShowSegment> {
    const response = await this.api.get(`/segments/${segmentId}`);
    return response.data;
  }

  // For You API
  async getForYouEpisodes(limit = 20): Promise<any[]> {
    const response = await this.api.get(`/episodes/for-you?limit=${limit}`);
    return response.data;
  }

  // Audio API
  async getAudioFile(segmentId: string, partIndex: number): Promise<Blob> {
    const response = await this.api.get(`/audio/segments/${segmentId}/${partIndex}`, {
      responseType: 'blob'
    });
    return response.data;
  }

  async getSegmentAudio(segmentId: string): Promise<Blob> {
    const response = await this.api.get(`/audio/segments/${segmentId}/main`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // Session Management (for starting playback sessions)
  async startSession(episodeId: string, sessionName: string): Promise<{
    session: { _id: string; sessionName: string; episodeId: string; status: string };
    episode: Episode;
    show: Show;
    channel: Channel;
    panelists: Panelist[];
    segments: ShowSegment[];
    message: string;
  }> {
    // For the consumption app, we'll create a virtual session that just loads the episode data
    // getEpisode already filters for published episodes, so this is safe
    const episode = await this.getEpisode(episodeId);
    const [show, channel, panelists, segments] = await Promise.all([
      this.getShow(episode.showId),
      this.getChannel(episode.channelId),
      this.getPanelists(),
      this.getShowSegments(episodeId)
    ]);

    // Create a virtual session object with the correct format: session_{episodeId}_{timestamp}
    const session = {
      _id: `session_${episodeId}_${Date.now()}`,
      sessionName,
      episodeId,
      status: 'active',
      startedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    return {
      session,
      episode,
      show,
      channel,
      panelists: panelists.filter(p => show.panelistIds.includes(p._id)),
      segments,
      message: 'Session started successfully'
    };
  }



  async getActiveSessions(limit = 20): Promise<any[]> {
    // Return empty array since we don't track sessions in consumption mode
    return [];
  }

  // Session Control API (not used in consumption mode)
  async pauseSession(sessionId: string): Promise<void> {
    throw new Error('Session control not available in consumption mode');
  }

  async resumeSession(sessionId: string): Promise<void> {
    throw new Error('Session control not available in consumption mode');
  }

  async skipCurrentArticle(sessionId: string): Promise<void> {
    throw new Error('Session control not available in consumption mode');
  }

  async endSession(sessionId: string): Promise<void> {
    throw new Error('Session control not available in consumption mode');
  }

  getPanelistHeadshotUrl(id: string): string {
    // For images, we need the full URL to the backend server
    const backendUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1';
    return `${backendUrl}/panelists/${id}/headshot`;
  }
}

export const apiService = new ApiService();
export default apiService;

